import React, { useEffect, useState } from 'react'
import { Checkbox, Form, Input, Modal, Radio, Spin, Switch } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import SgUpload from '@/components/upload/upload'
import { getQiniuImgUrl } from '@/config/qiniu'
import { shopStore } from '@/store/shop'
import { staffStore } from '@/store/staff'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getShopInfo, getShopMemberCardAdmin, updateMemberConfig } from '../api'
import { getMemberCardConfigSubmitParams, validateMemberCardContactMethod } from './member-card-setting-utils'
import SettingPage, { SettingActions } from './setting-page'

import './member-setting.scss'

interface MemberCardForm {
    card_face_theme: number
    app_recharge: boolean
    member_pay_password_enabled: boolean
    contact_method: number[]
    phone?: string
    wx_qr_code_img?: string
}

const themeList = [
    { value: 1, name: '橙色', className: 'orange' },
    { value: 2, name: '紫色', className: 'purple' },
    { value: 3, name: '绿色', className: 'green' },
]

const PayPasswordSwitch: React.FC<{
    checked?: boolean
    id?: string
    canEdit?: boolean
    onDenied?: () => void
    onChange?: (checked: boolean) => void
}> = ({ checked, id, canEdit = true, onDenied, onChange }) => (
    <Switch
        id={id}
        checked={checked}
        checkedChildren="开启"
        unCheckedChildren="关闭"
        onChange={checked => {
            if (!canEdit) {
                onDenied && onDenied()

                return
            }
            if (checked) {
                onChange && onChange(true)

                return
            }
            Modal.confirm({
                title: '关闭会员余额支付密码',
                content: '关闭后，会员使用余额支付时无需输入密码，请确认。',
                okText: '确认关闭',
                cancelText: '取消',
                onOk: () => onChange && onChange(false),
            })
        }}
    />
)

const MemberCardSetting: React.FC = () => {
    const [form] = Form.useForm<MemberCardForm>()
    const [cardTheme, setCardTheme] = useState(1)
    const [wxQrCodeImg, setWxQrCodeImg] = useState('')
    const [shopLogoImg, setShopLogoImg] = useState('')
    const [uploading, setUploading] = useState(false)

    const cardReq = useRequest(getShopMemberCardAdmin, {
        onError: e => $msg('error', e.message || '获取会员卡二维码失败'),
    })
    const shopReq = useRequest(getShopInfo, {
        onSuccess: res => {
            shopStore.setShopInfo(res)
            form.setFieldsValue({
                card_face_theme: res.membercard_config?.card_face_theme || 1,
                app_recharge: res.membercard_config?.app_recharge === 0,
                member_pay_password_enabled: res.membercard_config?.member_pay_password_enabled ?? true,
                contact_method: res.membercard_config?.contact_method_list || [],
                phone: res.phone,
                wx_qr_code_img: res.membercard_config?.wx_qr_code_img,
            })
            setCardTheme(res.membercard_config?.card_face_theme || 1)
            setWxQrCodeImg(res.membercard_config?.wx_qr_code_img || '')
            setShopLogoImg(getQiniuImgUrl(res.logo_image_url, 200, 200))
        },
        onError: e => $msg('error', e.message || '获取店铺信息失败'),
    })
    const updateReq = useRequest(updateMemberConfig, {
        manual: true,
        onSuccess: () => {
            $msg('success', '设置成功')
            shopReq.refresh()
        },
        onError: e => $msg('error', e.message || '设置失败'),
    })

    useEffect(() => {
        form.setFieldsValue({
            card_face_theme: 1,
            app_recharge: true,
            member_pay_password_enabled: true,
            contact_method: [0],
        })
    }, [form])

    const onSubmit = async () => {
        const values = await form.validateFields()
        const contactMethod = values.contact_method || []
        const contactError = validateMemberCardContactMethod(contactMethod, values)
        if (contactError) {
            $msg('warning', contactError)

            return
        }

        await updateReq.run(getMemberCardConfigSubmitParams(values))
    }

    const shopInfo = shopReq.data || shopStore.shopInfo
    const canEditPayPassword =
        !!shopStore.shopInfo &&
        !!staffStore.staffInfo &&
        shopStore.shopInfo.creator?.id === staffStore.staffInfo.user.id

    return (
        <SettingPage title="会员卡设置">
            <Spin spinning={shopReq.loading || cardReq.loading}>
                <div className="member-card-setting__layout">
                    <div className="member-card-setting__preview">
                        <div className={`member-card-setting__card theme-${cardTheme}`}>
                            {shopLogoImg ? (
                                <img className="member-card-setting__shop-logo" src={shopLogoImg} alt="店铺 Logo" />
                            ) : null}
                            <div className="member-card-setting__shop-name">{shopInfo?.shop_name || '店铺名称'}</div>
                            <div className="member-card-setting__card-title">会员卡</div>
                            <div className="member-card-setting__card-no">NO. 8888 8888</div>
                        </div>
                        <div className="member-card-setting__qr">
                            {cardReq.data?.acode_url ? (
                                <img src={cardReq.data.acode_url} alt="会员卡小程序码" />
                            ) : (
                                <div>暂无二维码</div>
                            )}
                            <span>顾客扫码后可领取会员卡</span>
                        </div>
                    </div>
                    <Form form={form} layout="vertical" className="member-card-setting__form">
                        <div className="member-card-setting__form-section-title">卡面与使用</div>
                        <Form.Item label="店铺 Logo">
                            <div className="member-card-setting__wx-code">
                                {shopLogoImg ? (
                                    <img src={shopLogoImg} alt="店铺 Logo" />
                                ) : (
                                    <div className="member-card-setting__wx-empty">未上传</div>
                                )}
                                <div className="member-card-setting__wx-actions">
                                    <SgUpload
                                        images
                                        accept="image/png,image/jpeg,image/jpg"
                                        onLoad={() => setUploading(true)}
                                        onSuccess={res => {
                                            updateMemberConfig({ shop_logo_img: res.key })
                                                .then(() => {
                                                    setShopLogoImg(getQiniuImgUrl(res.key, 200, 200))
                                                    if (shopStore.shopInfo) {
                                                        shopStore.setShopInfo({
                                                            ...shopStore.shopInfo,
                                                            logo_image_url: res.key,
                                                        })
                                                    }
                                                    $msg('success', '店铺 Logo 修改成功')
                                                })
                                                .catch(e => $msg('error', e.message || '修改店铺 Logo 失败'))
                                                .finally(() => setUploading(false))
                                        }}
                                        onError={e => {
                                            setUploading(false)
                                            $msg('error', e.message || '上传失败')
                                        }}
                                    >
                                        <Button types="primary" loading={uploading}>
                                            上传 Logo
                                        </Button>
                                    </SgUpload>
                                </div>
                            </div>
                        </Form.Item>
                        <Form.Item name="card_face_theme" label="卡面主题">
                            <Radio.Group onChange={e => setCardTheme(e.target.value)}>
                                {themeList.map(theme => (
                                    <Radio.Button key={theme.value} value={theme.value}>
                                        {theme.name}
                                    </Radio.Button>
                                ))}
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item name="app_recharge" label="小程序自助充值" valuePropName="checked">
                            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>
                        <Form.Item
                            name="member_pay_password_enabled"
                            label="会员余额支付密码"
                            valuePropName="checked"
                            extra="关闭后，会员使用余额支付时无需输入密码。"
                        >
                            <PayPasswordSwitch
                                canEdit={canEditPayPassword}
                                onDenied={() => $msg('error', '该设置仅管理员可修改，请让管理员开启或关闭')}
                            />
                        </Form.Item>
                        <div className="member-card-setting__form-section-title">店铺联系方式</div>
                        <Form.Item name="contact_method" label="展示方式">
                            <Checkbox.Group>
                                <Checkbox value={0}>手机号</Checkbox>
                                <Checkbox value={1}>微信二维码</Checkbox>
                            </Checkbox.Group>
                        </Form.Item>
                        <Form.Item
                            name="phone"
                            label="联系电话"
                            rules={[{ pattern: /^1\d{10}$/, message: '请输入正确的手机号' }]}
                        >
                            <Input placeholder="填写手机号后顾客可通过电话联系店铺" maxLength={11} />
                        </Form.Item>
                        <Form.Item name="wx_qr_code_img" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev.contact_method !== next.contact_method}>
                            {() =>
                                (form.getFieldValue('contact_method') || []).includes(1) ? (
                                    <Form.Item label="微信二维码">
                                        <div className="member-card-setting__wx-code">
                                            {wxQrCodeImg ? (
                                                <img src={getQiniuImgUrl(wxQrCodeImg, 160, 160)} alt="微信二维码" />
                                            ) : (
                                                <div className="member-card-setting__wx-empty">未上传</div>
                                            )}
                                            <div className="member-card-setting__wx-actions">
                                                <SgUpload
                                                    images
                                                    accept="image/png,image/jpeg,image/jpg"
                                                    onLoad={() => setUploading(true)}
                                                    onSuccess={res => {
                                                        setUploading(false)
                                                        setWxQrCodeImg(res.key)
                                                        form.setFieldsValue({ wx_qr_code_img: res.key })
                                                    }}
                                                    onError={e => {
                                                        setUploading(false)
                                                        $msg('error', e.message || '上传失败')
                                                    }}
                                                >
                                                    <Button types="primary" loading={uploading}>
                                                        上传二维码
                                                    </Button>
                                                </SgUpload>
                                                {wxQrCodeImg ? (
                                                    <Button
                                                        types="error"
                                                        onClick={() => {
                                                            setWxQrCodeImg('')
                                                            form.setFieldsValue({ wx_qr_code_img: undefined })
                                                        }}
                                                    >
                                                        删除
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>
                        <SettingActions>
                            <Auth perssion={20} onClick={onSubmit}>
                                <Button types="default" loading={updateReq.loading} disabled={updateReq.loading}>
                                    保存
                                </Button>
                            </Auth>
                        </SettingActions>
                    </Form>
                </div>
            </Spin>
        </SettingPage>
    )
}

export default MemberCardSetting
